import React, {useEffect, useMemo, useState} from 'react'
import styles from './UIGameUserSettings.module.scss'
import {AudioTabContent} from './AudioTabContent/index.ts'
import {ControlsTabContent} from './ControlsTabContent/index.ts'
import {DisplayTabContent} from './DisplayTabContent/index.ts'
import {GameInfoTabContent} from './GameInfoTabContent/index.ts'
import clsx from 'clsx'
import {type TranslationObject} from '@colonist/i18n'
import {T, t} from '@colonist/ui'
import IconInfo from '@static/images/icon_info.svg'
import IconDisplay from '@static/images/icon_display.svg'
import IconAudio from '@static/images/icon_audio.svg'
import IconControls from '@static/images/icon_controls.svg'
import IconArrow from '@static/images/icon_arrow.svg'
import {type ImageURL} from '@colonist/types'
import {useGameUserSettingsVolumeHotkeys} from '../../hooks/useGameUserSettingsVolume.ts'
import {useHasOnlyOneHumanPlayer} from '../../store/selectors/useHasOnlyOneHumanPlayer.ts'
import {useGameStore} from '../../store/GameStoreContext.ts'
import {useGameUserSettingsStorage} from '../../hooks/useGameUserSettingsStorage.ts'
import {useRequestPause} from '../../hooks/usePause.ts'

type SettingsTab = 'game-info' | 'display' | 'audio' | 'controls'

const SETTING_TABS: {
    id: SettingsTab
    label: TranslationObject
    icon: ImageURL
}[] = [
    {
        id: 'game-info',
        label: {key: 'strings:game.settings.gameInfoTab.title'},
        icon: IconInfo,
    },
    {
        id: 'display',
        label: {key: 'strings:game.settings.displayTab.title'},
        icon: IconDisplay,
    },
    {
        id: 'audio',
        label: {key: 'strings:game.settings.audioTab.title'},
        icon: IconAudio,
    },
    {
        id: 'controls',
        label: {key: 'strings:game.settings.controlsTab.title'},
        icon: IconControls,
    },
]

export interface UIGameUserSettingsProps {
    isNestedContentEnabled?: boolean
    enabledTabs?: SettingsTab[]
}

export const UIGameUserSettings = ({
    isNestedContentEnabled,
    enabledTabs,
}: UIGameUserSettingsProps) => {
    const isOpenSettings = useGameStore((state) => state.gameUserSettings.isOpenSettings)
    const setIsOpenSettings = useGameStore((state) => state.gameUserSettings.setIsOpenSettings)
    const requestPause = useRequestPause()
    const hasOnlyOneHumanPlayer = useHasOnlyOneHumanPlayer()
    useGameUserSettingsVolumeHotkeys()
    useGameUserSettingsStorage()
    const defaultTab = isNestedContentEnabled === true ? undefined : SETTING_TABS[0].id
    const [activeTab, setActiveTab] = useState<SettingsTab | undefined>(defaultTab)

    const tabs = useMemo(() => SETTING_TABS.filter((tab) => enabledTabs?.includes(tab.id) ?? true), [enabledTabs])

    useEffect(() => {
        if(isOpenSettings && hasOnlyOneHumanPlayer) {
            requestPause()
        }
    }, [isOpenSettings, hasOnlyOneHumanPlayer])

    if(isOpenSettings === false) return null

    const isLeftContainerEnabled = isNestedContentEnabled !== true || activeTab === undefined
    const isRightContainerEnabled = activeTab !== undefined

    const onClickBack = () => {
        if(isNestedContentEnabled && activeTab !== undefined) {
            setActiveTab(undefined)
        } else {
            setIsOpenSettings(false)
        }
    }

    return <div className={styles.gameSettingsContainer}>
        <div className={styles.headingContainer}>
            <img
                alt={t('strings:imgAlts.IconArrowPrevious')}
                className={styles.backButton}
                onClick={onClickBack}
                src={IconArrow}
            />
            <h1 className={styles.heading}><T id='strings:game.settings.title' /></h1>
        </div>
        <div className={styles.contentContainer}>
            {isLeftContainerEnabled && <div className={styles.contentLeft}>
                {tabs.map((tab) => (
                    <div
                        className={clsx(styles.tabButton, tab.id === activeTab && styles.active)}
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}>
                        <img alt={t(tab.label.key, tab.label.options)} className={styles.icon} src={tab.icon}/>
                        <T id={tab.label} />
                    </div>
                ))}
            </div>}
            {isRightContainerEnabled && <div className={styles.contentRight}>
                {activeTab === 'game-info' && <GameInfoTabContent />}
                {activeTab === 'display' && <DisplayTabContent />}
                {activeTab === 'audio' && <AudioTabContent />}
                {activeTab === 'controls' && <ControlsTabContent />}
            </div>}
        </div>
    </div>
}
